import { useLocation } from "react-router";

import {
  ClockRewind,
  InfoCircle,
  User,
  Commit,
  ExclamationMarkCircleFilled,
  GitBranch,
  GitPullRequest,
  Terminal,
} from "components/icons/generated";
import ManagedStateInfo from "components/ManagedStateInfo";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import MetaInfoProjectRoot from "components/MetaInfoList/ProjectRoot";
import MetaInfoVendor from "components/MetaInfoList/Vendor";
import SpacePath from "components/SpacePath";
import {
  getRunPriorityTooltip,
  runModuleProjectRootTooltipContent,
  runProjectRootTooltipContent,
  RunProposedTooltip,
  RunTrackedTooltip,
} from "components/TooltipSnippets";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { RUN_TYPES } from "constants/run";
import { VENDOR_CONFIG_TYPENAME } from "constants/vendor";
import useTypedContext from "hooks/useTypedContext";
import { RunPriorityPreset, VcsProvider } from "types/generated";
import { RUN_PRIORITY_PRESETS } from "shared/Run/priorityPresets";
import TriggerByInfo from "components/TriggerByInfo";
import RunStateBadge from "components/Run/elements/StateBadge";
import Icon from "ds/components/Icon";
import Tooltip from "ds/components/Tooltip";
import RunOriginDropdownLink from "components/Run/elements/RunOriginDropdownLink";
import { getCommitLabel } from "utils/commit";
import { providerHasVcsHost } from "utils/vcs";
import { isAnsibleStackVendor } from "utils/stack";
import useAnalytics, { AnalyticsPage } from "hooks/useAnalytics";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import { RunContext } from "components/Run/Context";
import { getRepoRevisionUrl } from "utils/spaceliftRepo";
import { generateBranchUrl } from "utils/urls";

import ExportTracesMenuItem from "../ExportTraces/MenuItem";
import RunHeaderChangesV3Nav from "./ChangesV3Nav";
import styles from "./styles.module.css";
import RunHeaderNonDetailedChangesNav from "./NonDetailedChangesNav";
import RunHeaderAnsibleChangesNav from "./AnsibleChangesNav";
import { showRuntimeConfigDetailsDrawer } from "../RuntimeConfigDetailsDrawer";

type RunHeaderProps = {
  analyticsPage?: AnalyticsPage;
};

const RunHeader = ({ analyticsPage }: RunHeaderProps) => {
  const location = useLocation();
  const prUrl = location.state?.prUrl;
  const prTitle = location.state?.prTitle;

  const { run, stack } = useTypedContext(RunContext);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: analyticsPage,
  });

  const isStack = stack.__typename === "Stack";
  const isTask = run.type === "TASK";
  const isAnsibleVendor = isStack && isAnsibleStackVendor(stack);

  const hasDetailedResources = run.supportsDetailedResources;

  const title = isTask ? run.command : run.title;
  const isTerraformVendor =
    isStack && stack.vendorConfig?.__typename === VENDOR_CONFIG_TYPENAME.TERRAFORM;
  const isSpaceliftProvider = stack.provider === VcsProvider.Spacelift;
  const hasVcsHost = providerHasVcsHost(stack.provider);

  const commitUrl =
    isSpaceliftProvider && stack.vcsIntegration
      ? getRepoRevisionUrl(stack.vcsIntegration.id, run.commit.hash)
      : run.commit.url;

  const commitLabel = getCommitLabel(stack.provider, run.commit.hash);

  const hasRuntimeConfig = !!run.runtimeConfig || !!run.customRuntimeConfig;

  return (
    <>
      <ViewHeader id="runViewHeader">
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle tooltipDelay={isTask ? 0 : 300}>{title}</ViewHeaderTitle>
            {run.priorityPreset !== RunPriorityPreset.Normal && (
              <Tooltip
                widthMode="maxWidthSm"
                on={(props) => (
                  <Icon
                    {...props}
                    aria-hidden={false}
                    aria-label={RUN_PRIORITY_PRESETS[run.priorityPreset].label}
                    src={RUN_PRIORITY_PRESETS[run.priorityPreset].icon}
                    color={RUN_PRIORITY_PRESETS[run.priorityPreset].color}
                  />
                )}
              >
                {getRunPriorityTooltip(run.priorityPreset)}
              </Tooltip>
            )}
            <RunStateBadge state={run.state} isForceApply={!!run.forceApplyMode} />
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <DropdownMenuEllipsis tooltip="Run actions" dotsSize="medium">
              <RunOriginDropdownLink runId={run.id} runCreatedAt={run.createdAt} />
              {hasRuntimeConfig && (
                <DropdownMenuItem
                  onAction={() => {
                    trackSegmentAnalyticsEvent?.("Runtime config details clicked");
                    showRuntimeConfigDetailsDrawer({
                      runtimeConfig: run.runtimeConfig?.yaml,
                      customRuntimeConfig: run.customRuntimeConfig?.yaml,
                    });
                  }}
                >
                  Runtime config details
                </DropdownMenuItem>
              )}
              <CopyFieldDropdownMenuItem title="Copy run ID" value={run.id} />
              <CopyFieldDropdownMenuItem
                title="Copy spacectl run logs command"
                value={`spacectl stack logs --id ${stack.id} --run ${run.id}`}
                onCopy={() => {
                  trackSegmentAnalyticsEvent?.("Copy spacectl run logs command clicked");
                }}
              />
              <ExportTracesMenuItem />
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>
        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            {stack.spaceDetails && (
              <SpacePath
                variant="meta"
                spaceId={stack.spaceDetails.id}
                fallbackName={stack.spaceDetails.name}
              />
            )}
            {isStack && (
              <MetaInfoVendor
                vendorConfig={stack.vendorConfig}
                runtimeConfig={run.runtimeConfig}
                runtimeConfigTerraformWorkflowTool={run.runtimeConfig?.terraform?.workflowTool}
                runtimeConfigTerraformVersion={run.runtimeConfig?.terraform?.version || undefined}
              />
            )}

            {run.runtimeConfig?.projectRoot && stack.apiHost && (
              <MetaInfoListItem
                icon={Terminal}
                target="_blank"
                linkText={run.runtimeConfig.projectRoot}
                href={generateBranchUrl({
                  apiHost: stack.apiHost,
                  namespace: stack.namespace,
                  repository: stack.repository,
                  repositoryURL: stack.repositoryURL,
                  provider: stack.provider,
                  branch: run.branch,
                  projectRoot: run.runtimeConfig.projectRoot,
                })}
              />
            )}

            {isStack && run.runtimeConfig?.projectRoot && !stack.apiHost && (
              <MetaInfoProjectRoot
                projectRoot={run.runtimeConfig.projectRoot}
                tooltip={runProjectRootTooltipContent}
              />
            )}

            {!isStack && run.runtimeConfig?.projectRoot && !stack.apiHost && (
              <MetaInfoProjectRoot
                projectRoot={run.runtimeConfig.projectRoot}
                tooltip={runModuleProjectRootTooltipContent}
              />
            )}

            {/* A provider without a VCS host has no branches: the branch stored on a
                Blob Storage run is only a placeholder for the HEAD pointer. */}
            {!isSpaceliftProvider && hasVcsHost && (
              <MetaInfoListItem icon={GitBranch}>{run.branch}</MetaInfoListItem>
            )}
            {!hasVcsHost && <MetaInfoListItem icon={Commit}>{commitLabel}</MetaInfoListItem>}

            {hasVcsHost && (
              <MetaInfoListItem
                href={commitUrl}
                linkText={commitLabel}
                icon={Commit}
                target="_blank"
              />
            )}

            {prUrl && (
              <MetaInfoListItem
                icon={GitPullRequest}
                href={prUrl}
                linkText={prTitle}
                target="_blank"
              />
            )}

            {run.commit.authorLogin && stack.provider === VcsProvider.Github && (
              <MetaInfoListItem
                icon={User}
                href={`https://github.com/${run.commit.authorLogin}`}
                linkText={run.commit.authorLogin}
                tooltip="Commit author"
              />
            )}

            {!run.commit.authorLogin ||
              (stack.provider !== VcsProvider.Github && (
                <MetaInfoListItem icon={User} tooltip="Commit author">
                  {run.commit.authorName}
                </MetaInfoListItem>
              ))}

            <TimestampMetaInfo
              prefix="Started on"
              icon={ClockRewind}
              timestamp={run.createdAt}
              postfix={
                <TriggerByInfo
                  stackId={stack.id}
                  triggeredBy={run.triggeredBy || undefined}
                  driftDetection={run.driftDetection}
                />
              }
            />

            <TimestampMetaInfo
              prefix="Committed on"
              icon={ClockRewind}
              timestamp={run.commit.timestamp}
            />

            {isTerraformVendor && stack.managesStateFile && (
              <MetaInfoListItem>
                <ManagedStateInfo />
              </MetaInfoListItem>
            )}

            {run.type === RUN_TYPES.TRACKED && (
              <MetaInfoListItem
                icon={InfoCircle}
                tooltip={RunTrackedTooltip}
                tooltipWidthMode="maxWidthSm"
              >
                <span className={styles.runType}>{run.type.toLocaleLowerCase()}</span>
              </MetaInfoListItem>
            )}
            {run.type === RUN_TYPES.PROPOSED && (
              <MetaInfoListItem
                icon={InfoCircle}
                tooltip={RunProposedTooltip}
                tooltipWidthMode="maxWidthSm"
              >
                <span className={styles.runType}>{run.type.toLocaleLowerCase()}</span>
              </MetaInfoListItem>
            )}
            {run.driftDetection && (
              <MetaInfoListItem icon={ExclamationMarkCircleFilled}>Drift detected</MetaInfoListItem>
            )}
          </MetaInfoList>
        </ViewHeaderScrollCollapse>
        {hasDetailedResources && !isAnsibleVendor && <RunHeaderChangesV3Nav />}
        {!hasDetailedResources && !isAnsibleVendor && <RunHeaderNonDetailedChangesNav />}
        {isAnsibleVendor && <RunHeaderAnsibleChangesNav />}
      </ViewHeader>
    </>
  );
};

export default RunHeader;
